import { Grid, Title, Text, Stack, Box, Button, NumberInput, SegmentedControl, LoadingOverlay, Image, Group, TextInput, em, Checkbox, Textarea } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectAvailabilityAndSettings, selectBookingCreateLoading, selectLoading, selectRedirectUrl, selectReservationContent } from '../../../redux/selectors/publicSelector';
import { createBooking, getAvailability } from '../../../redux/reducers/publicSlice';
import { AvailabilityDay } from '../../../models/availability.model';
import { Setting, SettingKey } from '../../../models/setting.model';
import { useForm, isEmail, isNotEmpty } from '@mantine/form';
import { Booking, BookingPaymentStatus, BookingSource, BookingType } from '../../../models/booking.model';
import { useMediaQuery } from '@mantine/hooks';
import { Show } from '../../../models/show.model';
import { checkBoxStyles, datePickerStyles, getInputStyles, getTextareaStyles, numberAdultInputStyles, numberInputStyles, textStyles } from './reservation.helper';
import { areAthensDatesEqual, getAthensDate, getStartOfDay } from '../../../utils/helper.utils';
import { DateTime } from 'luxon';
import busBanner from '/assets/images/bus-banner.png';
import vivaLogo from '/assets/images/viva-logo.png';
import './reservation.page.css';
import colors from '../../../utils/styles';
import DialogStatus from '../../common/dialog-status/dialog-status';
import { Logger } from 'utils/logger';
import { getDeviceInfo } from '../device-info/device-info';

enum ShowAvailabilityStatus {
    FULL = 'full',
    ALMOST_FULL = 'almostFull',
    LOW = 'low',
}

interface ReservationForm {
    date: Date | null;
    showTime: string | null;
    show_id: string | null;
    ticketType: string;
    adultTicketCount: number;
    childTicketCount: number;
    email: string;
    confirmEmail: string;
    fullName: string;
    phoneNumber: string;
    acceptTerms: boolean;
    notes: string;
}

const ReservationPage = ({ calendarRef }: { calendarRef: React.RefObject<HTMLDivElement> }) => {
    const dispatch = useDispatch();
    const isTablet = useMediaQuery(`(max-width: ${em(990)})`);
    const isDesktop = useMediaQuery(`(max-width: ${em(1024)})`);

    const [dateClicked, setDateClicked] = useState<Date | null>(null);

    const { availability, settings } = useSelector(selectAvailabilityAndSettings);
    const bookingCreateLoading = useSelector(selectBookingCreateLoading);
    const loading = useSelector(selectLoading);
    const redirectUrl = useSelector(selectRedirectUrl);
    const reservation = useSelector(selectReservationContent);

    const {
        isPaymentCustomerDetailsEnabled,
        limitedAvailabilityThreshold,
        fullShowColor,
        almostFullShowColor,
        availableShowColor,
        childTicketPrice,
        standardTicketPrice,
        vipTicketPrice,
        vipTicketChildPrice,
        additionalFeePercentage,
        maintainanceMode,
    } = useMemo(() => {
        let isPaymentCustomerDetailsEnabled = true;
        let limitedAvailabilityThreshold = 80;
        let fullShowColor = '#ff0000';
        let almostFullShowColor = '#ff6900';
        let availableShowColor = '#40ad26';
        let childTicketPrice = 65;
        let standardTicketPrice = 80;
        let vipTicketPrice = 105;
        let vipTicketChildPrice = 90;
        let additionalFeePercentage = 2.24;
        let maintainanceMode = false;

        settings.forEach((setting: Setting) => {
            if (setting.setting_key === SettingKey.ENABLE_PAYMENT_CUSTOMER_DETAILS) {
                isPaymentCustomerDetailsEnabled = setting.setting_value as boolean;
            }
            if (setting.setting_key === SettingKey.LIMITED_AVAILABILITY_THRESHOLD) {
                limitedAvailabilityThreshold = setting.setting_value as number;
            }
            if (setting.setting_key === SettingKey.FULL_SHOW_COLOR) {
                fullShowColor = setting.setting_value as string;
            }
            if (setting.setting_key === SettingKey.ALMOST_FULL_SHOW_COLOR) {
                almostFullShowColor = setting.setting_value as string;
            }
            if (setting.setting_key === SettingKey.AVAILABLE_SHOW_COLOR) {
                availableShowColor = setting.setting_value as string;
            }
            if (setting.setting_key === SettingKey.CHILD_TICKET_PRICE) {
                childTicketPrice = setting.setting_value as number;
            }
            if (setting.setting_key === SettingKey.STANDARD_TICKET_PRICE) {
                standardTicketPrice = setting.setting_value as number;
            }
            if (setting.setting_key === SettingKey.VIP_TICKET_PRICE) {
                vipTicketPrice = setting.setting_value as number;
            }
            if (setting.setting_key === SettingKey.VIP_TICKET_CHILD_PRICE) {
                vipTicketChildPrice = setting.setting_value as number;
            }
            if (setting.setting_key === SettingKey.ADDITIONAL_FEE_PERCENTAGE) {
                additionalFeePercentage = setting.setting_value as number;
            }
            if (setting.setting_key === SettingKey.MAINTENANCE_MODE) {
                maintainanceMode = setting.setting_value as boolean;
            }
        });

        return {
            isPaymentCustomerDetailsEnabled,
            limitedAvailabilityThreshold,
            fullShowColor,
            almostFullShowColor,
            availableShowColor,
            childTicketPrice,
            standardTicketPrice,
            vipTicketPrice,
            vipTicketChildPrice,
            additionalFeePercentage,
            maintainanceMode,
        }
    }, [settings]);

    const form = useForm<ReservationForm>({
        validateInputOnChange: true,
        initialValues: {
            date: null,
            showTime: null,
            show_id: null,
            ticketType: BookingType.STANDARD,
            adultTicketCount: 2,
            childTicketCount: 0,
            email: '',
            confirmEmail: '',
            fullName: '',
            phoneNumber: '',
            notes: '',
            acceptTerms: false,
        },
        validate: {
            fullName: isNotEmpty('Name cannot be empty'),
            email: isEmail('Invalid email'),
            confirmEmail: (value, values) =>
                !value ? 'Confirmation email is required' :
                    value !== values.email ? 'Emails must match' : null,
            phoneNumber: (value) => {
                if (!value) return 'Phone number is required';

                // Check if it contains only digits and plus
                const onlyDigitsOrPlus = /^[0-9+]+$/;
                if (!onlyDigitsOrPlus.test(value)) {
                    return 'Phone number must include only digits and plus(+)';
                }

                // Additional check: ensure total length doesn't exceed 15 digits
                if (value.length > 16) { // +15 digits = 16 characters max
                    return 'Phone number is too long (maximum 15 digits including country code)';
                }

                // ITU-T E.164 compliant validation
                // Must start with +, followed by 1-4 digit country code, then national number
                // Total length must be 7-15 digits (including country code)
                const e164Regex = /^\+[1-9]\d{6,14}$/;
                if (!e164Regex.test(value)) {
                    return 'Phone number must start with plus(+) followed by 1-4 digits (country code), then national number';
                }

                return null;
            },
            date: isNotEmpty('Please select a date'),
            acceptTerms: (value) => value === false ? 'You must accept the cancellation policy' : null,
            notes: (value) => value.length > 100 ? 'Notes cannot be more than 100 characters' : null,
            adultTicketCount: (value) => {
                if (isNaN(value) || value === null || value === undefined) return 'Please enter a valid number';
                if (value < 1) return 'Adult ticket count cannot be less than 1';
                return null;
            },
            childTicketCount: (value) => {
                if (isNaN(value) || value === null || value === undefined) return 'Please enter a valid number';
                if (value < 0) return 'Child ticket count cannot be less than 0';
                return null;
            },
        }
    });

    const getShowAvailabilityByOccupancy = (show: Show) => {
        const occupancyRatio = ((show.max_seats - show.available_total_seats) / show.max_seats) * 100;
        if (occupancyRatio === 1) {
            return ShowAvailabilityStatus.FULL;
        } else if (occupancyRatio < limitedAvailabilityThreshold) {
            return ShowAvailabilityStatus.LOW;
        } else {
            return ShowAvailabilityStatus.ALMOST_FULL;
        }
    }

    const isSelectedShowAlmostFull = useMemo(() => {
        const show = availability?.find((day: AvailabilityDay) => areAthensDatesEqual(day?.date, form.values.date))?.shows?.[0];
        if (!show) return false;
        return show.is_almost_full || getShowAvailabilityByOccupancy(show) === ShowAvailabilityStatus.ALMOST_FULL;
    }, [form.values.show_id, form.values.date, availability]);

    const totalPrice = useMemo(() => {
        let total = 0;

        if (form.values.ticketType === BookingType.STANDARD) {
            total = (form.values.adultTicketCount * standardTicketPrice) + (form.values.childTicketCount * childTicketPrice);
        } else {
            total = (form.values.adultTicketCount * vipTicketPrice) + (form.values.childTicketCount * vipTicketChildPrice);
        }
        const additionalFee = (total * additionalFeePercentage) / 100;
        return total + additionalFee;
    }, [form.values.ticketType, form.values.adultTicketCount, form.values.childTicketCount, standardTicketPrice, childTicketPrice, vipTicketPrice, vipTicketChildPrice, additionalFeePercentage]);

    const handleDateChange = (date: Date) => {
        // Convert the selected date to Athens timezone before extracting month/year
        const athensDate = getAthensDate(date, false) as DateTime;
        const currentMonth = (athensDate as DateTime).month;
        const currentYear = (athensDate as DateTime).year;
        dispatch(getAvailability({ month: currentMonth, year: currentYear } as any));
    }

    const getDateAvailability = (date: Date) => {
        // Convert browser's local date to Athens timezone for comparison
        const athensDate = getStartOfDay(date);
        const athensToday = getStartOfDay();

        // Check if the date is before today in Athens timezone
        if (athensDate < athensToday) return null;

        const dayData = availability.find((day: AvailabilityDay) => areAthensDatesEqual(day?.date, date));

        if (!dayData || !dayData?.shows?.length) return null;

        const show = dayData.shows[0]; // Assuming one show per day
        if (show.is_full || show.available_total_seats === 0) {
            return ShowAvailabilityStatus.FULL;
        }
        if (show.is_almost_full) {
            return ShowAvailabilityStatus.ALMOST_FULL;
        }

        return getShowAvailabilityByOccupancy(show);
    };

    const handleBooking = async (values: ReservationForm) => {
        // Create base date in Athens timezone
        const eventDate = getAthensDate(values.date, false) as DateTime;
        // Get the show time from the selected show
        const [hours, minutes] = values.showTime?.split(':').map(Number) || [20, 0];
        // Set the time in Athens timezone and let Luxon handle the DST conversion to UTC
        const athensTime = eventDate.set({ hour: hours, minute: minutes });
        // Convert to UTC by changing zone
        const bookingDate = athensTime.setZone('UTC').toJSDate();

        const booking: Partial<Booking> = {
            customer_name: values.fullName,
            customer_email: values.email,
            customer_phone: values.phoneNumber,
            show_id: values.show_id || '',
            booked_adult_seats: values.adultTicketCount,
            booked_children_seats: values.childTicketCount,
            booked_total_seats: values.adultTicketCount + values.childTicketCount,
            booking_type: values.ticketType as BookingType,
            booking_date: bookingDate,
            created_by: values.email || 'web_user',
            created_source: BookingSource.WEBSITE,
            total_price: Number(totalPrice?.toFixed(2) || 0),
            payment_status: BookingPaymentStatus.PENDING,
            booking_from_public_website: true,
            notes: values.notes,
        }
        dispatch(createBooking(booking as any));

        Logger.info(`Booking PUBLIC created by User ${values.email} - ${values.fullName}`, {
            booking: booking,
            deviceInfo: await getDeviceInfo(),
            dateClicked: dateClicked
        });
    }

    useEffect(() => {
        // Standard ticket type scenario where i start with 2 adult and 0 child, then i put 1 child and 1 adult and remove the child. 
        // I want to make sure that the adult ticket count is always 2.
        if (form.values.adultTicketCount === 1 && form.values.childTicketCount === 0) {
            form.setFieldValue('adultTicketCount', 2);
        }
    }, [form.values.ticketType, form.values.adultTicketCount, form.values.childTicketCount]);

    useEffect(() => {
        if (!!redirectUrl) {
            window.location.replace(redirectUrl);
        }
    }, [redirectUrl]);

    const convertTo12Hour = (time24: string) => {
        const [hours, minutes] = time24.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${hour12}:${minutes} ${ampm}`;
    };

    return <>
        {/* Reservation Section */}
        <Grid mt={isTablet ? 20 : 50} mx={isTablet ? '2%' : '0px'} justify="center" gutter={0}>
            <Grid.Col span={{ base: 12, md: 8 }} px="xl" mb={isTablet ? 20 : 10} >
                {!isDesktop && <Image src={busBanner} alt="bus" style={{ position: 'absolute', right: 50, width: '15%', height: '15%', rotate: '15deg' }} />}
                <Title size={36} style={{ color: colors.appYellow, fontFamily: "Playfair Display", transform: 'scaleY(1.2)', letterSpacing: '0.1em', fontSize: 'clamp(22px, 32px, 36px)' }}>
                    {reservation.title}
                </Title>
                <Text style={{ color: colors.appYellow, fontSize: 'clamp(1rem, 1.35rem, 1.55rem)', fontFamily: "Playfair Display", transform: 'scaleY(1.1)', letterSpacing: '0.05em' }}>
                    {reservation.description}
                </Text>
                {isDesktop && <Text style={{ marginTop: 10, color: 'black', fontSize: 'clamp(1rem, 1rem, 1.15rem)', fontWeight: 700, fontFamily: "Playfair Display", transform: 'scaleY(1.1)', letterSpacing: '0.05em' }}>
                    {reservation.transferNote}
                </Text>}
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 8 }} ref={calendarRef}>
                <form onSubmit={form.onSubmit(handleBooking)}>
                    <Text style={{ fontSize: isTablet ? 'clamp(0.5rem, 0.9rem, 1.1rem)' : '1.1rem', color: 'white' }}>
                        {reservation.instructions}
                    </Text>
                    <Box className="box-container">
                        <Stack gap="md" className="calendar-container" align="center">
                            <LoadingOverlay
                                visible={loading}
                                zIndex={1000}
                                overlayProps={{ radius: "md", blur: 1 }}
                                loaderProps={{
                                    color: colors.appPink,
                                    type: 'dots',
                                    size: 'sm'
                                }}
                                styles={{
                                    overlay: {
                                        backgroundColor: 'rgba(255, 20, 147, 0.2)',
                                    }
                                }}
                            />
                            <LoadingOverlay
                                visible={maintainanceMode}
                                zIndex={1000}
                                overlayProps={{ radius: "md", blur: 3 }}
                                loaderProps={{
                                    color: colors.appPink,
                                    type: 'dots',
                                    size: 'sm',
                                    children: <Text style={{ color: colors.appYellow, fontSize: '1.25rem', fontWeight: 'bold' }}>The website is under maintenance. Please try again later.</Text>
                                }}
                                styles={{
                                    overlay: {
                                        backgroundColor: 'rgba(255, 20, 147, 0.2)',
                                    }
                                }}
                            />

                            <DatePicker
                                allowDeselect
                                hideOutsideDates
                                value={form.values.date}
                                maxLevel="month"
                                onChange={(value) => {
                                    setDateClicked(value);
                                    const show = availability.find((day: AvailabilityDay) => areAthensDatesEqual(day?.date, value))?.shows?.[0];
                                    form.setFieldValue('date', getAthensDate(value) as any);
                                    form.setFieldValue('show_id', show?.id || '');
                                    form.setFieldValue('showTime', show?.show_time || '');
                                }}
                                onDateChange={handleDateChange}
                                classNames={{
                                    day: 'custom-calendar-day',
                                    calendarHeaderControl: 'custom-calendar-header-control',
                                    calendarHeaderLevel: 'custom-calendar-header-level',
                                }}
                                defaultDate={getAthensDate() as Date}
                                minDate={getAthensDate() as Date}
                                maxDate={(getAthensDate(null, false) as DateTime).set({ month: 11, day: 28 }).toJSDate() as Date}
                                getDayProps={(date) => {
                                    const availability = getDateAvailability(date);
                                    if (!availability) return {};

                                    const colors = {
                                        full: { backgroundColor: fullShowColor, cursor: 'not-allowed' },
                                        almostFull: { backgroundColor: almostFullShowColor },
                                        low: { backgroundColor: availableShowColor }
                                    };

                                    return {
                                        style: {
                                            ...colors[availability],
                                            color: 'white',
                                            ...(availability === ShowAvailabilityStatus.FULL && { pointerEvents: 'none' })
                                        },
                                        disabled: availability === ShowAvailabilityStatus.FULL
                                    };
                                }}
                                styles={datePickerStyles}
                            />

                            {isPaymentCustomerDetailsEnabled && (
                                <>
                                    <Group grow w="100%">
                                        <TextInput
                                            radius={0}
                                            label={reservation.form.email.label}
                                            placeholder={reservation.form.email.placeholder}
                                            required
                                            value={form.values.email}
                                            onChange={(event) => form.setFieldValue('email', event.currentTarget.value?.trim())}
                                            error={form.errors.email}
                                            styles={getInputStyles(form.errors.email) as any}
                                            variant="unstyled"
                                            classNames={{ root: 'calendar-input' }}
                                        />
                                        <TextInput
                                            radius={0}
                                            label="Confirm Email"
                                            placeholder="Confirm your email"
                                            required
                                            value={form.values.confirmEmail}
                                            onChange={(event) => form.setFieldValue('confirmEmail', event.currentTarget.value?.trim())}
                                            error={form.errors.confirmEmail}
                                            styles={getInputStyles(form.errors.confirmEmail) as any}
                                            variant="unstyled"
                                            classNames={{ root: 'calendar-input' }}
                                        />
                                    </Group>
                                    <Group grow w="100%">
                                        <TextInput
                                            radius={0}
                                            label={reservation.form.fullName.label}
                                            placeholder={reservation.form.fullName.placeholder}
                                            required
                                            value={form.values.fullName}
                                            onChange={(event) => form.setFieldValue('fullName', event.currentTarget.value)}
                                            error={form.errors.fullName}
                                            styles={getInputStyles(form.errors.fullName) as any}
                                            variant="unstyled"
                                            classNames={{ root: 'calendar-input' }}
                                        />
                                        <TextInput
                                            radius={0}
                                            label={reservation.form.phoneNumber.label}
                                            placeholder={reservation.form.phoneNumber.placeholder}
                                            required
                                            value={form.values.phoneNumber}
                                            onChange={(event) => form.setFieldValue('phoneNumber', event.currentTarget.value)}
                                            error={form.errors.phoneNumber}
                                            styles={getInputStyles(form.errors.phoneNumber) as any}
                                            variant="unstyled"
                                            classNames={{ root: 'calendar-input' }}
                                        />
                                    </Group>
                                </>
                            )}
                            <Group grow w="100%">
                                <Textarea
                                    radius={0}
                                    label={reservation.form.notes.label}
                                    placeholder={reservation.form.notes.placeholder}
                                    value={form.values.notes}
                                    onChange={(event) => form.setFieldValue('notes', event.currentTarget.value)}
                                    error={form.errors.notes}
                                    styles={getTextareaStyles(form.errors.notes) as any}
                                />
                            </Group>
                        </Stack>
                        <Stack gap="md" align="center">
                            <SegmentedControl
                                value={form.values.ticketType}
                                onChange={(value) => form.setFieldValue('ticketType', value)}
                                color={colors.appPink}
                                data={[
                                    { label: reservation.form.ticketTypes.standard, value: BookingType.STANDARD },
                                    { label: reservation.form.ticketTypes.golden, value: BookingType.VIP },
                                ]}
                                style={{ backgroundColor: colors.appYellow, padding: '10px', borderRadius: '10px' }}
                                transitionDuration={250}
                                transitionTimingFunction="linear"
                            />

                            <NumberInput
                                value={form.values.adultTicketCount}
                                onChange={(value) => form.setFieldValue('adultTicketCount', Number(value))}
                                min={form.values.childTicketCount > 0 ? 1 : 2}
                                max={(() => {
                                    const selectedDay = availability?.find((day: AvailabilityDay) => areAthensDatesEqual(day?.date, form.values.date));
                                    const availableAdultSeats = selectedDay?.shows?.[0]?.available_adult_seats ?? 50;
                                    return Math.min(50, availableAdultSeats);
                                })()}
                                label={`${reservation.form.adultTicket.label} ${form.values.ticketType === BookingType.STANDARD ? `${standardTicketPrice}€` : `${vipTicketPrice}€`}`}
                                clampBehavior="strict"
                                style={{ width: '150px' }}
                                styles={numberAdultInputStyles as any}
                                description={reservation.form.adultTicket.description}
                                allowNegative={false}
                                allowDecimal={false}
                            />
                            <NumberInput
                                value={form.values.childTicketCount}
                                onChange={(value) => form.setFieldValue('childTicketCount', Number(value))}
                                min={0}
                                max={(() => {
                                    const selectedDay = availability?.find((day: AvailabilityDay) => areAthensDatesEqual(day?.date, form.values.date));
                                    const availableChildrenSeats = selectedDay?.shows?.[0]?.available_children_seats ?? 5;
                                    return Math.min(5, availableChildrenSeats);
                                })()}
                                label={`${reservation.form.childTicket.label} ${form.values.ticketType === BookingType.STANDARD ? `${childTicketPrice}€` : `${vipTicketChildPrice}€`}`}
                                clampBehavior="strict"
                                style={{ width: '150px' }}
                                styles={numberInputStyles as any}
                                description={reservation.form.childTicket.description}
                                allowNegative={false}
                                allowDecimal={false}
                            />
                            <Text style={textStyles}>
                                {reservation.form.total.replace('{totalPrice}', totalPrice.toFixed(2))}
                            </Text>
                            <Text style={textStyles}>
                                {form.values.date
                                    ? `${reservation.form.date.label} ${form.values.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                                    : reservation.form.date.placeholder
                                }<br />
                                {form.values.showTime && `${reservation.form.showTime} ${form.values.showTime} (${convertTo12Hour(form.values.showTime)})`}
                                {form.values.date && isSelectedShowAlmostFull && <Text style={{
                                    fontSize: '1rem', color: almostFullShowColor
                                }}>
                                    {reservation.form.almostFull}
                                </Text>}
                            </Text>

                            <Checkbox
                                checked={form.values.acceptTerms}
                                onChange={(event) => form.setFieldValue('acceptTerms', event.currentTarget.checked)}
                                label={reservation.form.cancellationCheckbox}
                                error={form.errors.acceptTerms}
                                color={colors.appPink}
                                styles={checkBoxStyles}
                            />

                            <Button
                                disabled={!form.isValid()}
                                loading={bookingCreateLoading}
                                rightSection={<Image src={vivaLogo} alt="viva" height={10} style={{ maxWidth: '60px' }} />}
                                size="sm"
                                radius="xl"
                                style={{ backgroundColor: colors.appPink, '&:hover': { backgroundColor: colors.appLightestBlue }, boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.5)' }}
                                type="submit">
                                {reservation.form.payNow}
                            </Button>
                            <Text style={{ fontSize: '0.70rem', color: 'white', fontWeight: 'bold' }}>
                                {reservation.form.paymentNote}
                                <br />
                                {reservation.form.additionalFees.replace('{additionalFeePercentage}', additionalFeePercentage.toFixed(2))}
                                <br />
                                <br />
                                {reservation.form.cancelationPolicy}
                                <br />
                                {reservation.form.dateChangesHeader}
                                {reservation.form.dateChangesDescription}
                                <br />
                                {reservation.form.dateChangesNoteStandard}
                                <br />
                                {reservation.form.dateChangesNoteGolden}
                            </Text>
                        </Stack>
                    </Box>
                </form>
            </Grid.Col>
            <DialogStatus
                dialogProps={{
                    position: { left: '37%', bottom: 20 },
                    styles: {
                        root: {
                            backgroundColor: colors.appPink,
                        },
                        closeButton: {
                            color: colors.appWhite,
                        },
                    }
                }}
                textProps={{
                    style: {
                        fontSize: '1rem',
                        fontFamily: "Helvetica",
                        textAlign: 'center',
                        marginRight: '10px'
                    }
                }}
                showButton={false}
                errorColor={colors.appWhite}
                successColor={colors.appYellow}
            />
        </Grid>
    </>
};

export default ReservationPage;